import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, EmptyState, Field } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import VideoCall from "@/components/VideoCall";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lock, CheckCircle2, Circle, CalendarClock, Video, ExternalLink, Clock, Gift } from "lucide-react";

const fmtDay = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmt = (d) => (d ? new Date(d).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

/** Minutes until a timestamp (negative once it has passed). */
const minsUntil = (iso) => (iso ? Math.round((new Date(iso) - new Date()) / 60000) : null);

export function StudentTasks({ onChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scheduleFor, setScheduleFor] = useState(null);
  const [when, setWhen] = useState("");
  const [gift, setGift] = useState("");
  const [call, setCall] = useState(null);
  const [, setTick] = useState(0);

  const load = () => api.get("/my-tasks").then(({ data }) => setData(data)).catch(() => setData({ tasks: [] }));
  useEffect(() => {
    load();
    const t = setInterval(() => setTick((n) => n + 1), 30000); // keeps the start-window countdown fresh
    return () => clearInterval(t);
  }, []);

  const refresh = () => { load(); onChanged?.(); };

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openSchedule = (task) => {
    setScheduleFor(task);
    setGift(task.gift_card_choice || task.meeting?.gift_card_choice || "");
    const d = new Date(Date.now() + 24 * 3600 * 1000);
    d.setMinutes(0, 0, 0);
    setWhen(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
  };

  const sendRequest = () => {
    if (!when) return toast.error("Pick a date and time");
    const iso = new Date(when).toISOString();
    const task = scheduleFor;
    setScheduleFor(null);
    act(() => (task.meeting
      ? api.post(`/task-meetings/${task.meeting.id}/propose`, { scheduled_start: iso, gift_card_choice: gift })
      : api.post(`/my-tasks/${task.id}/schedule`, { scheduled_start: iso, gift_card_choice: gift })),
    "Sent to your Campus Coordinator");
  };

  const startMeeting = async (m) => {
    setBusy(true);
    try {
      await api.post(`/task-meetings/${m.id}/start`);
      const { data: tok } = await api.post(`/video-meetings/${m.id}/token`);
      setCall({ id: m.id, roomUrl: tok.room_url, token: tok.token, isOwner: tok.is_owner });
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const closeCall = async () => {
    if (call) { try { await api.post(`/video-meetings/${call.id}/end`); } catch { /* room already closed */ } }
    setCall(null); refresh();
  };

  if (!data) return null;
  const tasks = data.tasks || [];
  const done = tasks.filter((t) => t.status === "Complete").length;

  return (
    <Card title="Your checklist" data-testid="student-tasks">
      {tasks.length === 0 ? <EmptyState text="No tasks assigned yet — your Campus Coordinator will add them." /> : (
        <>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-2 flex-1 bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full bg-cc-green transition-all" style={{ width: `${(done / tasks.length) * 100}%` }} />
            </div>
            <span className="text-xs text-zinc-500" data-testid="tasks-progress">{done} of {tasks.length} done</span>
          </div>
          {data?.rotation_start && tasks.some((t) => t.offset_days) && (
            <p className="text-xs text-zinc-400 -mt-2 mb-3" data-testid="tasks-rotation-note">
              Dated tasks open on their own schedule — counted from your rotation start ({fmtDay(data.rotation_start)}){data.rotation_end ? `, your rotation end (${fmtDay(data.rotation_end)})` : ""} or your graduation date.
            </p>
          )}

          <div className="space-y-3">
            {tasks.map((t) => {
              const m = t.meeting;
              const complete = t.status === "Complete";
              const openIn = m?.can_start_at ? minsUntil(m.can_start_at) : null;
              return (
                <div key={t.id} data-testid={`task-${t.order}`}
                  className={`border rounded-md p-4 ${complete ? "bg-green-50/40 border-green-200" : t.locked ? "opacity-60" : "bg-white"}`}>
                  <div className="flex items-start gap-3">
                    {complete ? <CheckCircle2 className="w-5 h-5 text-cc-green shrink-0 mt-0.5" />
                      : t.locked ? <Lock className="w-4 h-4 text-zinc-400 shrink-0 mt-1" data-testid={`task-locked-${t.order}`} />
                        : <Circle className="w-5 h-5 text-cc-blue shrink-0 mt-0.5" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <p className={`text-sm font-medium ${complete ? "text-zinc-500" : "text-zinc-900"}`}>
                          <span className="text-zinc-400 mr-1.5">{t.order}.</span>{t.title}
                        </p>
                        <StatusBadge status={t.status} />
                      </div>
                      {t.description && <p className="text-xs text-zinc-500 mt-1">{t.description}</p>}

                      {t.locked && (
                        <p className="text-xs text-zinc-400 mt-2" data-testid={`task-lock-reason-${t.order}`}>
                          {t.locked_by_order ? "Unlocks when you finish the task above." : t.not_yet_reason || "Not open yet."}
                        </p>
                      )}
                      {!t.locked && !complete && t.offset_days ? (
                        <p className="text-xs text-cc-blue mt-1" data-testid={`task-scheduled-${t.order}`}>Scheduled check-in — open now</p>
                      ) : null}

                      {m && (
                        <div className="mt-3 border-t pt-3 text-xs space-y-1.5" data-testid={`task-meeting-${t.order}`}>
                          <p className="text-zinc-600 flex items-center gap-1.5">
                            <CalendarClock className="w-3.5 h-3.5 text-cc-blue" />
                            <b>{fmt(m.scheduled_start)}</b> with {m.cc_name}
                          </p>
                          {m.gift_card_choice && (
                            <p className="text-zinc-500 flex items-center gap-1.5" data-testid={`task-gift-${t.order}`}>
                              <Gift className="w-3.5 h-3.5 text-cc-orange" />Gift card: {m.gift_card_choice}
                            </p>
                          )}
                          {m.status === "Pending Confirmation" && (m.awaiting_me
                            ? <p className="text-orange-700">{m.proposed_by_name} proposed this time — please confirm it.</p>
                            : <p className="text-amber-700">Waiting for {m.cc_name} to confirm.</p>)}
                          {m.status === "Scheduled" && openIn > 0 && (
                            <p className="text-zinc-500 flex items-center gap-1.5" data-testid={`task-countdown-${t.order}`}>
                              <Clock className="w-3.5 h-3.5" />
                              Start Meeting opens {m.start_window_minutes} minutes before — in {openIn >= 60 ? `${Math.floor(openIn / 60)}h ${openIn % 60}m` : `${openIn}m`}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-2 pt-1">
                            {m.awaiting_me && m.status === "Pending Confirmation" && (
                              <Button size="sm" disabled={busy} className="bg-cc-green text-white h-7 text-xs"
                                onClick={() => act(() => api.post(`/task-meetings/${m.id}/confirm`), "Meeting confirmed")}
                                data-testid={`confirm-meeting-${t.order}`}>Confirm this time</Button>
                            )}
                            {["Pending Confirmation", "Scheduled"].includes(m.status) && (
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openSchedule(t)}
                                data-testid={`propose-time-${t.order}`}>Propose another time</Button>
                            )}
                            {["Scheduled", "Started"].includes(m.status) && (
                              <Button size="sm" disabled={busy || !m.can_start_now} onClick={() => startMeeting(m)}
                                className={m.can_start_now ? "bg-cc-orange text-white h-7 text-xs" : "h-7 text-xs bg-zinc-200 text-zinc-500 cursor-not-allowed"}
                                title={m.can_start_now ? "" : `Opens ${m.start_window_minutes} minutes before your meeting`}
                                data-testid={`start-meeting-${t.order}`}>
                                <Video className="w-3 h-3 mr-1" />{m.status === "Started" ? "Join Meeting" : "Start Meeting"}
                              </Button>
                            )}
                          </div>
                        </div>
                      )}

                      {!t.locked && !complete && !m && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {t.kind === "meeting" && (
                            <Button size="sm" className="bg-cc-blue text-white h-7 text-xs" onClick={() => openSchedule(t)}
                              data-testid={`schedule-task-${t.order}`}><CalendarClock className="w-3 h-3 mr-1" />Set up my meeting</Button>
                          )}
                          {t.kind === "link" && t.link_url && (
                            <a href={t.link_url} target="_blank" rel="noreferrer">
                              <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`open-link-${t.order}`}>
                                <ExternalLink className="w-3 h-3 mr-1" />Open
                              </Button>
                            </a>
                          )}
                          {t.kind === "paperwork" && (
                            <a href="/paperwork"><Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`open-paperwork-${t.order}`}>Go to my paperwork</Button></a>
                          )}
                          {t.kind !== "meeting" && (
                            <Button size="sm" disabled={busy} className="bg-cc-green text-white h-7 text-xs"
                              onClick={() => act(() => api.post(`/my-tasks/${t.id}/complete`, {}), "Task complete")}
                              data-testid={`complete-task-${t.order}`}>Mark as done</Button>
                          )}
                        </div>
                      )}

                      {(t.activity || []).length > 0 && (
                        <ul className="mt-3 space-y-0.5" data-testid={`task-activity-${t.order}`}>
                          {t.activity.slice(-4).map((a, i) => (
                            <li key={i} className="text-[11px] text-zinc-400">{new Date(a.at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · {a.by_name}: {a.text}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <Dialog open={!!scheduleFor} onOpenChange={(o) => !o && setScheduleFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{scheduleFor?.meeting ? "Propose another time" : "Set up your meeting"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="border rounded-md p-3 bg-zinc-50" data-testid="schedule-cc-confirm">
              <p className="text-xs text-zinc-500">Your Campus Coordinator</p>
              <p className="text-sm font-medium text-zinc-900">{data.coordinator?.name || "Not assigned yet"}</p>
              {data.coordinator?.email && <p className="text-xs text-zinc-400">{data.coordinator.email}</p>}
            </div>
            <Field label="Date & time you'd like to meet">
              <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="schedule-when" />
            </Field>
            {scheduleFor?.gift_card_prompt && (
              <Field label="Lunch gift card choice">
                <Select value={gift} onValueChange={setGift}>
                  <SelectTrigger data-testid="schedule-gift-card"><SelectValue placeholder={(data.gift_cards || []).length ? "Pick your gift card" : "No choices set up yet"} /></SelectTrigger>
                  <SelectContent>{(data.gift_cards || []).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-zinc-400 mt-1">Lunch is on us — pick the card you'd like. You can change it before you meet.</p>
              </Field>
            )}
            <p className="text-xs text-zinc-400">{data.coordinator?.name || "Your coordinator"} will confirm this time or propose another. You'll both see it on your dashboards, and Start Meeting opens {data.start_window_minutes || 15} minutes before.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleFor(null)}>Cancel</Button>
            <Button onClick={sendRequest} disabled={busy || !data.coordinator} className="bg-cc-green text-white" data-testid="send-schedule-btn">
              {scheduleFor?.meeting ? "Send new time" : "Send request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {call && <VideoCall roomUrl={call.roomUrl} token={call.token} userName={user.name} isOwner={call.isOwner} onClose={closeCall} />}
    </Card>
  );
}
